/**
 * The conversation, rendered. Story VC-016.
 *
 * One renderer for both session kinds, which is what `src/thread.js` was for
 * and the reason is unchanged: a voice session's turns come from
 * `voice-client-core`'s transcript state and a text session's from the
 * runtime's own events, but what a reader sees is one conversation. Two
 * renderers would be two places for the interruption marker to drift.
 *
 * An agent turn carries the screen's half under its prose, because that is
 * where it belongs in a conversation — the sentence, then what the sentence
 * was about, then what to say next.
 */

import { AnswerScreen } from "./Answer.jsx";
import { Icon } from "./Icon.jsx";

/**
 * @typedef {import("../session/thread.js").Turn} Turn */

/**
 * @param {{ turns: Turn[], onChoose: (text: string) => void,
 *           busy?: boolean, emptyMessage?: string }} props
 */
export function Thread({ turns, onChoose, busy = false, emptyMessage }) {
  if (turns.length === 0) {
    return <p className="empty">{emptyMessage}</p>;
  }
  return (
    <>
      {turns.map((turn, index) => (
        <TurnView
          key={turn.id}
          turn={turn}
          onChoose={onChoose}
          // Only the last turn may still be arriving, so only it gets the
          // caret — a caret on an earlier turn would say an answer that
          // finished is still coming.
          streaming={!turn.final && index === turns.length - 1}
          busy={busy}
        />
      ))}
    </>
  );
}

/**
 * @param {{ turn: Turn, onChoose: (text: string) => void,
 *           streaming: boolean, busy: boolean }} props
 */
function TurnView({ turn, onChoose, streaming, busy }) {
  const mine = turn.speaker === "user";
  return (
    <div className={`turn turn--${turn.speaker}`}>
      {turn.text ? (
        <div className="bubble">
          {turn.text}
          {streaming ? <span className="bubble__caret" /> : null}
        </div>
      ) : null}

      {turn.attachments ? (
        // §40.9.1 returns an attachment as a reference whose bytes no route
        // yet serves (RP-008), so the honest rendering is to say one was
        // there. A turn that read as empty because the user sent a
        // photograph is the defect VC-024 exists to stop, one layer on.
        <span className="turn__attachments">
          <Icon name="warning" size={12} />{" "}
          {turn.attachments === 1
            ? "1 attachment"
            : `${turn.attachments} attachments`}
        </span>
      ) : null}

      {turn.interrupted ? (
        // The marker is a UI affordance, never text in the transcript
        // (`voice_server_lld.md` §21.4): a marker inside the text would be
        // read aloud by anything that speaks it back.
        <span className="turn__interrupted">
          <Icon name="stop" size={12} /> interrupted
        </span>
      ) : null}

      {!mine && turn.snapshot ? (
        <AnswerScreen
          snapshot={turn.snapshot}
          onChoose={onChoose}
          busy={busy}
        />
      ) : null}
    </div>
  );
}
